//PACKAGES
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

//CONSTANTS
import { STRING, STRINGS } from "../constant";

//COMPONENTS
import { Header, SpeedoMeter, Chart } from "../components";

//PACKAGES
import _ from "lodash";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { AuthContext } from "../context/AuthProvider";
import { IMAGES, SVG_IMAGES } from "../assets";
import { useContext, useEffect, useState } from "react";
import { ThemeContext } from "../context/ThemeProvider";
import { MixpanelContext } from "../context/MixpanelProvider";
import BackArrowIcon from "../components/BackArrowIcon";
import { getPracticeTestIconBg } from "../utils/themeUtils";
import { createLogger } from "../utils/logger";

// Card animations
const cardVariants = {
  hover: { 
    scale: 1.02,
    boxShadow: "0px 8px 15px rgba(0,0,0,0.1)",
    transition: { type: "spring", stiffness: 400, damping: 10 }
  }
};

function History() {
  const navigation = useNavigate();
  const { profile, loggingEnabled } = useContext(AuthContext);
  const { trackHistory } = useContext(MixpanelContext);
  const { theme, currentTheme } = useContext(ThemeContext);

  const [error, setError] = useState("");
  const [historyData, setHistoryData] = useState([]);
  const [totalTest, setTotalTest] = useState(0);
  const [avgScore, setAvgScore] = useState(0);
  const navigate = useNavigate();
  console.log("loggingEnabled", loggingEnabled);

  const logger = createLogger(loggingEnabled, 'History');

  // Helper function to get icon background color
  const getIconBg = () => {
    return getPracticeTestIconBg(theme, currentTheme);
  }

  // Helper function to get card background
  const getCardBackground = (iconBg) => {
    const baseColor = currentTheme === 'light' ? theme?.homeCard?.start_light : theme?.homeCard?.start_dark;
    const darkerShade = currentTheme === 'light' ? theme?.homeCard?.end_light : theme?.homeCard?.end_dark;
    return {
      background: `linear-gradient(to right, ${baseColor}, ${darkerShade})`,
    };
  };

  // Helper function for text color
  const getTextColor = () => {
    return currentTheme === 'light' 
      ? theme?.text?.light
      : theme?.text?.dark;
  };

  useEffect(() => {
    const loadHistoryData = async () => {
      try {
        trackHistory();

        logger.log("profile before await:", profile);
        const profileData = await profile;
        logger.log("profile after await:", profileData);
        
        const array = profileData?.practice_test_history ?? [];
        logger.log("practice_test_history", array);
        setTotalTest(array.length);

        const correctArray = array.map((e) => parseInt(e?.correct ?? 0));
        const totalCorrect = _.sum(correctArray);

        const totalQuestionArray = array.map((e) => parseInt(e?.total ?? 0));
        const totalQuestions = _.sum(totalQuestionArray);

        const scoresArray = array.map((e) => parseInt(e?.score ?? 0));
        const totalScore = _.sum(scoresArray);
        let avg = ((totalCorrect / totalQuestions) * 100) ?? 0;
        
        avg = isNaN(avg) ? 0 : parseInt(avg);
        setAvgScore(avg);    

        const groupping = _.chain(array)
          .groupBy("date")
          .map((value, key) => ({ title: key, data: value.reverse() }))
          .value();
        const message = array.length == 0 ? STRING.no_record_test_histoy : "";
        setError(message);
        setHistoryData(groupping.reverse());
      } catch (error) {
        logger.error("Error loading history data:", error);
        setError("Error loading history data");
      }
    };

    if (profile) {
      loadHistoryData();
    }
  }, [profile]);

  return (
    <div className="p-6">
      <div className="flex items-center mb-6">
        <button
          className="rounded-lg border-2 p-2 mr-4 flex items-center justify-center"
          style={{ borderColor: currentTheme === "light" ? theme?.border_color?.light : theme?.border_color?.dark }}
          onClick={() => navigate(-1)}
        >
          <BackArrowIcon
            color={currentTheme === "light" ? theme?.text?.light : theme?.text?.dark}
          />
        </button>
        <h1 
          className="text-2xl font-semibold"
          style={{ color: getTextColor() }}
        >
          Test History
        </h1>
      </div>

      {historyData.length > 0 && (
        <div className="grid grid-cols-12 gap-[24px] px-[24px]">
          <div className="col-span-12 md:col-span-6 h-full">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="rounded-2xl flex flex-col items-center justify-center mt-10 p-6"
              style={getCardBackground()}
            >
              <div className="w-1/2 h-1/2 mt-10">
                <CircularProgressbar
                  value={isNaN(avgScore) ? 0 : avgScore}
                  text={`${isNaN(avgScore) ? 0 : avgScore}%`}
                  circleRatio={0.75}
                  background
                  backgroundPadding={6}
                  styles={buildStyles({
                    pathColor: getIconBg(),
                    textColor: getTextColor(),
                    textSize: "1.1rem",
                    backgroundColor: currentTheme === 'light' ? theme?.homeCard?.start_light : theme?.homeCard?.start_dark,
                    trailColor: currentTheme === 'light' ? theme?.homeCard?.end_light : theme?.homeCard?.end_dark,
                    rotation: 1 / 2 + 1 / 8,
                  })}
                />
              </div>
              <span className="mb-10 text font-normal" style={{ color: getTextColor() }}>
                {STRINGS.overall_average_score}
              </span>
            </motion.div>
          </div>
          <div className="col-span-12 md:col-span-6 gap-[24px]">
            <div
              className="flex flex-col md:overflow-y-auto md:h-[75vh] md:border-[rgba(0,0,0,0.1)] md:dark:border-[rgba(255,255,255,0.5)] md:rounded-[20px] md:border-[1px] md:px-[24px] md:pb-[24px]"
            >
              <AnimatePresence>
                {historyData.length > 0 &&
                  historyData?.map((e, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: idx * 0.1 }}
                    >
                      <div
                        className="mt-[24px]"
                        style={{ color: getTextColor() }}
                      >
                        {e?.title}
                      </div>
                      {e?.data?.map((item, index) => (
                        <HistoryItem
                          data={item}
                          key={index + "0"}
                          onClick={() => {
                            navigation("/testReview", { state: { historyData: item, canGoBackKey: false } });
                          }}
                          theme={theme}
                          currentTheme={currentTheme}
                          getIconBg={getIconBg}
                          getCardBackground={getCardBackground}
                          getTextColor={getTextColor}
                        />
                      ))}
                    </motion.div>
                  ))}
              </AnimatePresence>
            </div>
          </div>
        </div>
      )}
      {error && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center justify-center h-[50vh]"
        >
          <div className="flex flex-col items-center justify-center">
            <img className="w-12 h-auto" src={IMAGES.ic_tab_hisotry} />
            <div
              className="mb-4 flex items-center justify-center line-clamp-1 truncate px-[24px] text-center break-normal"
              style={{ color: getTextColor() }}
            >
              {error}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function HistoryItem({ data, onClick, theme, currentTheme, getIconBg, getCardBackground, getTextColor }) {
  return (
    <motion.div
      variants={cardVariants}
      whileHover="hover"
      onClick={onClick}
      className="flex text-lg items-center w-full p-4 mt-4 cursor-pointer rounded-lg shadow-md transition-colors duration-300"
      style={getCardBackground()}
    >
      <div 
        className="flex items-center justify-center w-10 h-10 rounded-lg"
        style={{ backgroundColor: getIconBg() }}
      >
        <img
          className="filter brightness-0"
          src={SVG_IMAGES.ic_practice_test}
          alt="Test Icon"
        />
      </div>
      <div className="flex-1 mx-3">
        <div className="font-medium" style={{ color: getTextColor() }}>
          {data?.test_name}
        </div>
        <div className="text-sm" style={{ color: getTextColor() }}>
          Questions: {data?.total}, Score: {data?.score}%
        </div>
      </div>
      <div className="text-xl" style={{ color: getTextColor() }}>›</div>
    </motion.div>
  );
}

export default History;

